/**
 * fetch-based HTTP client
 * Provides CSRF token and Buildkite-specific headers automatically
 */

// Get CSRF token from meta tag (set by Rails)
function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content ?? "";
}

// Get default headers (computed at request time)
function getDefaultHeaders(): Record<string, string> {
  return {
    "X-CSRF-Token": getCsrfToken(),
    "X-Buildkite-Frontend-Version": BUILDKITE_FRONTEND_VERSION ?? "unknown",
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export class HttpError extends Error {
  constructor(readonly response: Response) {
    super(`HTTP ${response.status}: ${response.statusText} - ${response.url}`);
    this.name = "HttpError";
  }

  get status() {
    return this.response.status;
  }

  get statusText() {
    return this.response.statusText;
  }
}

/**
 * Request function with automatic CSRF and headers
 */
export async function request<T>(
  url: string,
  options: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...requestOptions } = options;

  const requestInit: RequestInit = {
    credentials: "same-origin", // Include cookies for authentication
    ...requestOptions,
    headers: {
      ...getDefaultHeaders(),
      ...requestOptions.headers,
    },
  };

  // If json is provided, stringify it and set as body
  if (json !== undefined) {
    requestInit.body = JSON.stringify(json);
  }

  const response = await fetch(url, requestInit);

  if (!response.ok) {
    throw new HttpError(response);
  }

  // Handle non-JSON responses
  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) {
    // For non-JSON responses (like 204 No Content), return null
    // This is more honest than undefined and indicates "no data"
    return null as T;
  }

  return (await response.json()) as T;
}

/**
 * Convenience methods for common HTTP verbs
 */
export const getJson = <T>(url: string, options: RequestInit = {}): Promise<T> =>
  request<T>(url, options);

export const postJson = <T>(url: string, json: unknown, options: RequestInit = {}): Promise<T> =>
  request<T>(url, { method: "POST", json, ...options });

export const putJson = <T>(url: string, json: unknown, options: RequestInit = {}): Promise<T> =>
  request<T>(url, { method: "PUT", json, ...options });

export const deleteJson = <T>(url: string, options: RequestInit = {}): Promise<T> =>
  request<T>(url, { method: "DELETE", ...options });

/**
 * Utility for handling multiple promises
 * Resolves when all promises resolve, or rejects when any reject
 */
export const whenAll = Promise.all.bind(Promise);

// CSRF token is read dynamically on each request from meta tags
// CSRF token is automatically included in all requests
